// What this client's work feeds, and who may be told about it.
//
// **Nothing here is authored.** Every connection is read back out of a description:
// a project or artifact whose prose mentions another one is saying it came out of
// that work, and the edge runs from the mentioned record to the mentioning one. So
// the way to add a connection is to write the sentence on the record, and the way to
// remove one is to stop saying it. There is no connection editor, because a
// connection is not a thing you can hold — it is what the writing already claims.
//
// **Visibility is not on the edge either.** It is on the two records the edge joins,
// and a connection is only as visible as the less visible of them. Two gates still
// have to agree before one client is NAMED on another's page:
//
//   the team   marks the WORK as something that may be named (`visibility`)
//   the far org  has agreed to be named at all (`naming` on the org record)
//
// Either one missing and the connection is still there, drawn against an unnamed
// node. Dropping it would shrink the web and misrepresent its shape, which is the
// thing this view exists to show (client-surface.md §6).

import { Link, useNavigate } from 'react-router-dom'

import { Pill, Section } from '../components/common'
import { EcosystemGraph } from '../components/EcosystemGraph'
import { Icon } from '../components/Icon'
import { CONSENT_LABELS, labelOf, VISIBILITY_LABELS } from '../lib/model'
import { clientBands } from '../lib/network'
import {
  artifactsForOrg,
  connectionsForOrg,
  fieldStr,
  namedOnOthersPage,
  namingConsent,
  orgOfRecord,
  projectsForOrg,
  titleOf,
  visibilityOf,
  type EntityRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'

export function EcosystemTab({ org }: { org: EntityRecord }) {
  const { state, version } = useStore()
  const navigate = useNavigate()
  void version

  const edges = connectionsForOrg(state, org.id)
  const mine = new Set([
    ...projectsForOrg(state, org.id).map((w) => w.id),
    ...artifactsForOrg(state, org.id).map((a) => a.id),
  ])
  const shown = edges.filter((e) => e.visibility !== 'internal').length
  const inWeb = new Set(edges.flatMap((e) => [e.from, e.to]))
  const unconnected = [...mine].filter((id) => !inWeb.has(id)).length

  return (
    <>
      {edges.length > 0 && (
        <Section
          title="The web"
          note="builds on, above · their work, middle · what it feeds, below"
        >
          <div
            className="rounded-control border p-2"
            style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
          >
            <EcosystemGraph
              state={state}
              edges={edges}
              bands={clientBands(edges, mine)}
              emphasis={mine}
              onOpen={(id) => navigate(`/e/${id}`)}
            />
          </div>
          {/* This is the team's view and it shows every name. The dashed ring is a
              preview of the client's version, not a redaction of this one. */}
          <div className="meta mt-2 flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="flex items-center gap-1.5">
              <svg width="11" height="11">
                <circle cx="5.5" cy="5.5" r="4.5" fill="var(--marine)" />
              </svg>
              Their work
            </span>
            <span className="flex items-center gap-1.5">
              <svg width="11" height="11">
                <circle
                  cx="5.5"
                  cy="5.5"
                  r="4.5"
                  fill="var(--panel)"
                  stroke="var(--marine)"
                  strokeWidth="1.5"
                />
              </svg>
              Named on their page
            </span>
            <span className="flex items-center gap-1.5">
              <svg width="11" height="11">
                <circle
                  cx="5.5"
                  cy="5.5"
                  r="4.5"
                  fill="var(--panel)"
                  stroke="var(--faint)"
                  strokeWidth="1.5"
                  strokeDasharray="3 2.5"
                />
              </svg>
              Named here, anonymous to them
            </span>
            <span>Dashed line · internal only</span>
            {unconnected > 0 && (
              <span className="ml-auto">{unconnected} not connected to anything yet</span>
            )}
          </div>
        </Section>
      )}

      <Section title="Connections" note="written in a description, not set here">
        {edges.length === 0 ? (
          <p className="note py-2">
            No connections yet. Mention another project or artifact in a description and the
            connection appears here: "Grew out of @Union catalogue" points the arrow.
          </p>
        ) : (
          <>
            <p className="meta pb-2">
              {shown} of {edges.length} visible to {fieldStr(org, 'name') || 'this client'}. Each
              one comes from the sentence beneath it, and each is only as visible as the less
              visible of the two records it joins.
            </p>
            <div className="rows">
              {edges.map((e) => {
                // The far end is whichever side is not theirs.
                const far = mine.has(e.from) ? e.to : e.from
                const farOrg = orgOfRecord(state, far)
                const named = namedOnOthersPage(state, far)
                const consent = namingConsent(farOrg)
                const vis = labelOf(VISIBILITY_LABELS, e.visibility)
                // Which end is holding the edge back, so the fix has an address.
                const weakest =
                  visibilityOf(state.entities[e.from]) === e.visibility ? e.from : e.to

                return (
                  <div key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5">
                    <span className="text-body max-w-full min-w-[14rem] flex-1 overflow-hidden">
                      <Link to={`/e/${e.from}`} className="hover:underline">
                        {titleOf(state, e.from) || 'Untitled'}
                      </Link>
                      <Icon
                        name="ArrowRight"
                        size={12}
                        className="mx-1.5 inline"
                        style={{ color: 'var(--faint)' }}
                      />
                      <Link to={`/e/${e.to}`} className="font-medium hover:underline">
                        {titleOf(state, e.to) || 'Untitled'}
                      </Link>
                      {/* The sentence is the authority. Showing it means the row can
                          be argued with at its source rather than here. */}
                      {/* One line. A sentence naming six records is one sentence, and
                          it would otherwise repeat in full under all six rows; the
                          record itself is where you read the whole thing. */}
                      {e.label && (
                        <span className="meta block truncate italic" title={e.label}>
                          {e.label}
                        </span>
                      )}
                      {farOrg && (
                        <span className="meta block">
                          {fieldStr(farOrg, 'name')}
                          {' · '}
                          {named === 'named'
                            ? 'named on their page'
                            : named === 'anonymous'
                              ? 'shown unnamed on their page'
                              : 'not on their page'}
                        </span>
                      )}
                    </span>

                    {/* Read-only on purpose: the setting lives on the record, which is
                        the thing the decision is actually about. */}
                    <Link
                      to={`/e/${weakest}`}
                      className="shrink-0 hover:underline"
                      title="Set on the record itself"
                    >
                      <Pill
                        def={vis ?? { key: e.visibility, color: 'grey' }}
                        label={vis?.label ?? e.visibility}
                      />
                    </Link>

                    {/* What the FAR ORG has decided. Not ours to set from here — it
                        is a field on their own record, which is where the decision
                        belongs. */}
                    <span className="shrink-0" title="Whether that client agreed to be named">
                      {farOrg ? (
                        <Pill
                          def={labelOf(CONSENT_LABELS, consent) ?? { key: consent, color: 'grey' }}
                          label={labelOf(CONSENT_LABELS, consent)?.label ?? consent}
                        />
                      ) : (
                        <span className="meta">no client</span>
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </Section>

      <Section title="Being named elsewhere" note="this client's own answer">
        <p className="meta py-2">
          {namingConsent(org) === 'approved'
            ? `${fieldStr(org, 'name')} may be named on other clients' pages.`
            : namingConsent(org) === 'requested'
              ? `Asked. Until they answer, they appear unnamed on other clients' pages.`
              : namingConsent(org) === 'declined'
                ? `Declined. They appear unnamed on other clients' pages.`
                : `Not asked. They appear unnamed on other clients' pages.`}{' '}
          Set it under Details.
        </p>
      </Section>
    </>
  )
}
